'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { BarChart3, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Target, Zap } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid,  } from 'recharts';

const monthlyRevenue = [
  { month: 'Jan', actual: 54.8, forecast: 58.0 },
  { month: 'Feb', actual: 71.3, forecast: 68.5 },
  { month: 'Mar', actual: 83.7, forecast: 79.0 },
  { month: 'Apr', actual: 91.2, forecast: 85.0 },
  { month: 'May', actual: 68.5, forecast: 88.0 },
  { month: 'Jun', actual: null, forecast: 92.0 },
  { month: 'Jul', actual: null, forecast: 96.5 },
];

const sectorRevenue = [
  { sector: 'Residential', revenue: 148.2, growth: 12.4 },
  { sector: 'Commercial', revenue: 94.7, growth: -3.1 },
  { sector: 'Industrial', revenue: 67.3, growth: 8.9 },
  { sector: 'Vacant Land', revenue: 52.1, growth: -11.2 },
  { sector: 'Farmland', revenue: 88.4, growth: 5.7 },
  { sector: 'Public-Land', revenue: 41.8, growth: 14.3 },
  { sector: 'Mixed Use', revenue: 117.3, growth: 7.2 },
];

const countyPerformance = [
  { county: 'Montserrado', target: 280, actual: 241, pct: 86 },
  { county: 'Nimba', target: 95, actual: 62, pct: 65 },
  { county: 'Bong', target: 72, actual: 68, pct: 94 },
  { county: 'Grand Bassa', target: 88, actual: 79, pct: 90 },
  { county: 'Margibi', target: 64, actual: 41, pct: 64 },
  { county: 'Maryland', target: 48, actual: 44, pct: 92 },
  { county: 'Lofa', target: 55, actual: 38, pct: 69 },
];

const kpis = [
  { label: 'YTD Revenue', value: '$509.8M', change: '+14.2%', up: true, color: 'var(--accent)' },
  { label: 'Annual Target', value: '$1.176B', change: '43.4% achieved', up: true, color: '#60a5fa' },
  { label: 'Top Tax Category', value: 'Residential', change: '+12.4% growth', up: true, color: '#34d399' },
  { label: 'Strongest County', value: 'Bong', change: '94% of target', up: true, color: '#4ade80' },
  { label: 'Weakest County', value: 'Margibi', change: '64% of target', up: false, color: '#f87171' },
];

export default function RevenueAnalyticsClient() {
  const [activeView, setActiveView] = useState<'trend' | 'sector' | 'county'>('trend');

  return (
    <AppLayout>
      <div className="min-h-screen px-4 py-5 lg:px-6 xl:px-8 max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #b8860b 0%, #d4a017 100%)' }}>
              <BarChart3 size={20} style={{ color: '#070e1f' }} />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold" style={{ color: 'var(--dark-text)' }}>Revenue Analytics</h1>
              <p className="text-xs sm:text-sm" style={{ color: 'var(--dark-muted)' }}>AI forecasting · Property type trends · County performance · Revenue intelligence</p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{ backgroundColor: 'rgba(212,160,23,0.15)', color: 'var(--accent)', border: '1px solid rgba(212,160,23,0.3)' }}>
            <Zap size={12} />
            AI Forecast Active
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
          {kpis.map((k) => (
            <div key={k.label} className="rounded-xl px-4 py-4"
              style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
              <p className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>{k.label}</p>
              <p className="text-xl font-bold font-mono-data mb-1" style={{ color: k.color }}>{k.value}</p>
              <div className="flex items-center gap-1">
                {k.up ? <ArrowUpRight size={12} style={{ color: '#34d399' }} /> : <ArrowDownRight size={12} style={{ color: '#f87171' }} />}
                <span className="text-xs" style={{ color: k.up ? '#34d399' : '#f87171' }}>{k.change}</span>
              </div>
            </div>
          ))}
        </div>

        {/* View tabs */}
        <div className="flex flex-wrap gap-1 p-1 rounded-lg mb-4"
          style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}>
          {[
            { id: 'trend' as const, label: 'Revenue Trend' },
            { id: 'sector' as const, label: 'LRA Real Property Tax — Property Types' },
            { id: 'county' as const, label: 'County Performance' },
          ].map((tab) => (
            <button key={tab.id} onClick={() => setActiveView(tab.id)}
              className="px-4 py-2 rounded-md text-xs font-medium transition-all"
              style={{
                backgroundColor: activeView === tab.id ? 'var(--dark-elevated)' : 'transparent',
                color: activeView === tab.id ? 'var(--accent)' : 'var(--dark-muted)',
                border: activeView === tab.id ? '1px solid var(--dark-border)' : '1px solid transparent',
              }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Charts */}
        {activeView === 'trend' && (
          <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>Monthly Revenue vs AI Forecast (USD M)</p>
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-1.5"><div className="w-3 h-0.5 rounded" style={{ backgroundColor: '#d4a017' }} /><span style={{ color: 'var(--dark-muted)' }}>Actual</span></div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-0.5 rounded border-dashed" style={{ backgroundColor: '#60a5fa' }} /><span style={{ color: 'var(--dark-muted)' }}>AI Forecast</span></div>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={monthlyRevenue}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--dark-border)" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: 'var(--dark-muted)' }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 12, fill: 'var(--dark-muted)' }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)', borderRadius: '8px', fontSize: '12px' }} />
                <Line type="monotone" dataKey="actual" stroke="#d4a017" strokeWidth={2.5} dot={{ fill: '#d4a017', r: 4 }} connectNulls={false} name="Actual" />
                <Line type="monotone" dataKey="forecast" stroke="#60a5fa" strokeWidth={2} strokeDasharray="5 5" dot={false} name="AI Forecast" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {activeView === 'sector' && (
          <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
            <p className="text-sm font-semibold mb-1" style={{ color: 'var(--dark-text)' }}>LRA Real Property Tax — Revenue by Property Type (USD M) — YTD 2025</p>
            <p className="text-xs mb-4" style={{ color: 'var(--dark-muted)' }}>Residential · Commercial · Industrial · Vacant Land · Farmland · Public-Land Improvements · Mixed Use</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={sectorRevenue} margin={{ top: 0, right: 0, left: -10, bottom: 0 }}>
                  <XAxis dataKey="sector" tick={{ fontSize: 10, fill: 'var(--dark-muted)' }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: 'var(--dark-muted)' }} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)', borderRadius: '8px', fontSize: '11px' }} />
                  <Bar dataKey="revenue" radius={[4, 4, 0, 0]} fill="#d4a017" name="Revenue (M)" />
                </BarChart>
              </ResponsiveContainer>
              <div className="space-y-3">
                {sectorRevenue.map((s) => (
                  <div key={s.sector} className="flex items-center justify-between p-3 rounded-lg"
                    style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}>
                    <div>
                      <p className="text-sm font-medium" style={{ color: 'var(--dark-text)' }}>{s.sector}</p>
                      <p className="text-xs font-mono-data" style={{ color: 'var(--accent)' }}>${s.revenue}M</p>
                    </div>
                    <div className="flex items-center gap-1">
                      {s.growth > 0 ? <TrendingUp size={13} style={{ color: '#34d399' }} /> : <TrendingDown size={13} style={{ color: '#f87171' }} />}
                      <span className="text-xs font-semibold" style={{ color: s.growth > 0 ? '#34d399' : '#f87171' }}>
                        {s.growth > 0 ? '+' : ''}{s.growth}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeView === 'county' && (
          <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
            <p className="text-sm font-semibold mb-4" style={{ color: 'var(--dark-text)' }}>County Revenue Performance vs Target (USD M)</p>
            <div className="space-y-3">
              {countyPerformance.map((c) => (
                <div key={c.county} className="p-3 rounded-lg" style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium" style={{ color: 'var(--dark-text)' }}>{c.county}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-mono-data" style={{ color: 'var(--dark-muted)' }}>${c.actual}M / ${c.target}M</span>
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: c.pct >= 85 ? 'rgba(52,211,153,0.15)' : c.pct >= 70 ? 'rgba(251,191,36,0.15)' : 'rgba(248,113,113,0.15)', color: c.pct >= 85 ? '#34d399' : c.pct >= 70 ? '#fbbf24' : '#f87171' }}>
                        {c.pct}%
                      </span>
                    </div>
                  </div>
                  <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--dark-surface)' }}>
                    <div className="h-full rounded-full transition-all" style={{ width: `${c.pct}%`, backgroundColor: c.pct >= 85 ? '#34d399' : c.pct >= 70 ? '#fbbf24' : '#f87171' }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
